import { FaMinus, FaPlus } from "react-icons/fa";
import { useGetLocalizedText } from "../../../hooks/localization";
import classNames from "./TextCustomization.module.css";
import { useCallback, useEffect, useMemo } from "react";
import { LayoutState } from "../../../types";
import useLayout from "../../../hooks/layout";
import { CaptionText } from "../Captions";
import { useOrganization } from "../../context/organization/organization";
import Slider from "../Slider";

function TextCustomization() {
  const getLocalizedText = useGetLocalizedText();

  const colorOptions = useMemo(() => {
    const options: {
      text: LayoutState["settings"]["textColor"];
      background: LayoutState["settings"]["textBackgroundColor"];
    }[] = [
      {
        text: "#101010",
        background: "#ccc",
      },
      {
        text: "#fff",
        background: "#101010",
      },
      {
        text: "#101010",
        background: "#fff",
      },
      {
        text: "#fff",
        background: "#ff5314",
      },
      {
        text: "#fff",
        background: "#3c75ee",
      },
    ];
    return options;
  }, []);

  const [layout, setLayout] = useLayout();

  const { setOrganization } = useOrganization();
  useEffect(() => {
    if (!setOrganization) {
      return;
    }
    setOrganization((prevState) => {
      if (!prevState) {
        return prevState;
      }

      return {
        ...prevState,
        default_text_translation_setting: {
          ...prevState.default_text_translation_setting,
          text_size: layout.settings.textSize,
          text_color: layout.settings.textColor,
          text_background_color: layout.settings.textBackgroundColor,
        },
      };
    });
  }, [setOrganization, layout.settings]);

  const minTextSize = 12;
  const maxTextSize = 38;

  const setTextSize = useCallback(
    (textSize: number) => {
      setLayout((prevState) => ({
        ...prevState,
        settings: {
          ...prevState.settings,
          textSize: textSize,
        },
      }));
    },
    [setLayout]
  );

  const clamp = useCallback((value: number) => {
    return Math.min(Math.max(value, minTextSize), maxTextSize);
  }, []);

  const incrTextSize = useCallback(
    (incrBy: number) => {
      setLayout((prevState) => ({
        ...prevState,
        settings: {
          ...prevState.settings,
          textSize: clamp(prevState.settings.textSize + incrBy),
        },
      }));
    },
    [setLayout, clamp]
  );

  return (
    <div className={classNames.TextCustomization}>
      <div>
        <label>Text size</label>
        <CaptionText text="Welcome to Langfinity!" edited={false} />
        <div className={classNames.TextSizeSlider}>
          <FaMinus onClick={() => incrTextSize(-1)} />
          <Slider
            min={minTextSize}
            max={maxTextSize}
            value={layout.settings.textSize}
            onChange={(value) => setTextSize(value)}
            label="Text size"
          />
          <FaPlus onClick={() => incrTextSize(1)} />
        </div>
      </div>
      <div>
        <label>Text color</label>
        <div className={classNames.TextCaptionExamples}>
          {colorOptions.map((option, index) => (
            <div
              key={index}
              style={{
                border:
                  layout.settings.textColor === option.text &&
                  layout.settings.textBackgroundColor === option.background
                    ? "2px solid #2672ED"
                    : "2px solid transparent",
                color: option.text,
                backgroundColor: option.background,
              }}
              onClick={() =>
                setLayout((prevState) => ({
                  ...prevState,
                  settings: {
                    ...prevState.settings,
                    textColor: option.text,
                    textBackgroundColor: option.background,
                  },
                }))
              }
            >
              {getLocalizedText("TEXT")}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default TextCustomization;
